<template>
    <div class="cart-item row">
        <!-- course image -->
        <div
            class="course-img"
            :style="'background-image: url(' + cartItem.image + ')'"
        >
            <div class="overlay" :class="overlayColor(index)"></div>
        </div>
        <!-- course info: type, title and duration -->
        <div class="course-info col-12 col-sm-grow col-md-4 col-lg-6">
            <div class="course-type row items-center">
                <template
                    v-if="
                        cartItem.courses_coursetype.slug ===
                        slugs.courseTypes.online
                    "
                >
                    <q-icon name="language" class="q-mr-sm"></q-icon>
                    {{ $t('general.online') }}
                </template>
                <template
                    v-if="
                        cartItem.courses_coursetype.slug ===
                        slugs.courseTypes.onsite
                    "
                >
                    <q-icon name="business" class="q-mr-sm"></q-icon>
                    {{ $t('general.presential') }}
                </template>
            </div>
            <router-link
                :to="{
                    name: 'CourseDetails',
                    params: { id: cartItem.id },
                }"
            >
                <h5>{{ JSONParseTranslations(cartItem.title, locale.key) }}</h5>
            </router-link>
            <p>{{ $t('courses.hours', { hours: cartItem.duration }) }}</p>
        </div>
        <!-- cart and wishlist actions -->
        <div
            class="course-actions col-grow col-sm-shrink col-md-grow capitalize-text"
        >
            <template v-if="!changesDisallowed">
                <span @click="removeFromCart(cartItem)">
                    {{ $t('actions.remove') }}
                </span>
                <span @click="moveToWishlist(cartItem)">
                    {{ $t('actions.move_to_wishlist') }}
                </span>
            </template>
        </div>
        <!-- prices and units -->
        <div class="course-price col-12 col-sm-12 col-md text-right">
            <div class="row justify-end q-mt-sm">
                <div
                    v-if="
                        user && user.userRoles.name === slugs.userRoles.client
                    "
                    class="course-quantity col-auto"
                >
                    <q-btn
                        v-if="!changesDisallowed"
                        icon="remove"
                        color="primary"
                        class="q-mb-sm"
                        size="sm"
                        dense
                        :disable="cartItem.quantity <= 1"
                        :ripple="false"
                        @click="updateQuantity(-1)"
                    >
                    </q-btn>
                    <span class="q-mx-md q-my-sm">{{ cartItem.quantity }}</span>
                    <span v-if="changesDisallowed">UN</span>
                    <q-btn
                        v-if="!changesDisallowed"
                        icon="add"
                        color="primary"
                        class="q-mb-sm"
                        size="sm"
                        dense
                        :ripple="false"
                        @click="updateQuantity(1)"
                    >
                    </q-btn>

                    <div class="text-caption">
                        <span class="text-uppercase"
                            >{{ $t('cart.unit_price_abbreviation') }} ({{
                                $t('general.without_vat')
                            }})</span
                        >:
                        <!-- normal price -->
                        <span
                            :class="[
                                { 'text-strike': cartItem.promotion_discount },
                                { 'text-body1': !cartItem.promotion_discount },
                            ]"
                            >{{
                                new Intl.NumberFormat(
                                    currency.definition['locale'],
                                    {
                                        style: 'currency',
                                        currency: currency.key,
                                    }
                                ).format(
                                    parseFloat(
                                        JSON.parse(cartItem.price)[currency.key]
                                    )
                                )
                            }}</span
                        >
                        <!-- promotion discount price -->
                        <span
                            v-if="cartItem.promotion_discount"
                            class="text-body1 q-ml-sm text-secondary"
                            >{{
                                new Intl.NumberFormat(
                                    currency.definition['locale'],
                                    {
                                        style: 'currency',
                                        currency: currency.key,
                                    }
                                ).format(discountedPrice)
                            }}</span
                        >
                    </div>
                    <!-- discount badge and value -->
                    <div class="price q-mb-sm text-caption">
                        <span
                            v-if="cartItem.promotion_discount"
                            class="text-secondary"
                        >
                            <q-icon
                                name="
                        sell"
                            >
                            </q-icon>
                            {{
                                cartItem.promotion_discount +
                                '% ' +
                                $t('general.discount')
                            }}
                        </span>
                    </div>
                </div>
                <div v-if="hasRowDiscount" class="text-caption">
                    {{ $t('orders.this_item_has_a_trade_discount_of') }}
                    {{ cartItem.row_discount_percentage.toFixed(2) }}%
                </div>
                <div class="price">
                    <span>
                        ({{ cartItem.vat.value
                        }}{{ $t('general.vat_percentage') }})
                    </span>
                    {{
                        new Intl.NumberFormat(currency.definition['locale'], {
                            style: 'currency',
                            currency: currency.key,
                        }).format(totalPriceWithVat)
                    }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import translate from '@/local-shared/mixins/translate'
import store from '@/store'
import { slugs } from '@/local-shared/mixins/slugs'

export default {
    name: 'CourseCartItem',

    mixins: [translate],

    props: {
        index: {
            type: Number,
            default: 0,
        },

        cartItem: {
            type: Object,
            default: () => ({
                id: null,
                title: '',
                coursetype_id: null,
                duration: 0,
                price: 0,
                quantity: 0,
            }),
        },

        user: {
            type: Object,
            default: () => ({}),
        },

        changesDisallowed: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            quantity: this.cartItem.quantity,

            slugs: slugs,
        }
    },

    computed: {
        discountedPrice() {
            let price = 0
            if (this.currency) {
                price = parseFloat(
                    JSON.parse(this.cartItem.price)[this.currency.key]
                )
                price = price * (1 - this.cartItem.promotion_discount / 100)
            }
            return price.toFixed(2)
        },

        totalPrice() {
            let price = 0
            if (this.currency) {
                price = parseFloat(
                    JSON.parse(this.cartItem.price)[this.currency.key]
                )
                price =
                    price *
                    (1 - this.cartItem.promotion_discount / 100) *
                    this.cartItem.quantity
            }
            return price.toFixed(2)
        },

        totalPriceWithVat() {
            let price = 0
            if (this.currency) {
                price =
                    parseFloat(
                        JSON.parse(this.cartItem.price)[this.currency.key]
                    ) * this.cartItem.quantity
                price = price * (1 - this.cartItem.promotion_discount / 100) // apply discount

                if (this.hasRowDiscount) {
                    // if it's a contract item apply contract line discount
                    price =
                        price *
                        (1 - this.cartItem.row_discount_percentage / 100)
                }

                if (this.hasGlobalDiscount) {
                    // if it's a contract item apply contract global discount
                    price = price * (1 - this.cartItem.global_discount / 100)
                }

                price = price * (1 + parseInt(this.cartItem.vat.value) / 100) // apply vat on the discounted/not price
            }
            return price.toFixed(2)
        },

        locale() {
            return store.getters['locale/currentLocale']
        },

        currency() {
            return store.getters['currency/currentCurrency']
        },

        hasRowDiscount() {
            return 'row_discount_percentage' in this.cartItem
        },

        hasGlobalDiscount() {
            return 'global_discount' in this.cartItem
        },
    },

    watch: {
        cartItem(value) {
            this.quantity = value.quantity
        },
    },

    methods: {
        overlayColor(index) {
            return 'overlay-color-' + (index % 3)
        },

        updateQuantity(change) {
            let quantityUpdated = this.cartItem.quantity
            change === 1 ? quantityUpdated++ : quantityUpdated--

            if (quantityUpdated > 0) {
                let params = {
                    course: {
                        id: this.cartItem.id,
                    },
                    quantity: quantityUpdated,
                }
                store.dispatch('cart/setQuantity', params)
            }
        },

        removeFromCart(cartItem) {
            this.$emit('removecourse', cartItem)
        },

        moveToWishlist(cartItem) {
            this.$emit('movetowishlist', cartItem)
        },
    },
}
</script>
